import React, { FC, ReactElement, useRef, useState } from 'react';
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@material-ui/core';
import { CsvBuilder } from 'filefy';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAppData } from '../../providers';
import { useStyles } from './styles';
import { PDFFileIcon, ExcelFileIcon, ExportIcon, SendEmailIcon } from '../CustomIcons';
import { trackAmplitudeData } from '../../services/amplitudeService';
import { PrintButton } from '../PrintButton';
import { ColumnConfig } from '../../pages/Reports/DataTable/types';

interface Props {
  title: string;
  fontSize?: number;
  exportFileName: string;
  columns: ColumnConfig[];
  allData: any[];
  selectedColumns: string[];
  totalsRowBuilder?: (visibleCols: ColumnConfig[]) => string[];
  content?: ReactElement;
  printColumnNumber?: number;
  enableButtons: boolean;
  translation: any;
  reportNameInEn: string;
  sendEmail?: () => void;
}

export const buildExportData = (allData: any[], columns: ColumnConfig[], selectedColumns: string[], totalsRowBuilder?: (visibleCols: ColumnConfig[]) => string[]): string[][] => {
  const visible = selectedColumns.length === 0 ? columns : columns.filter((col) => selectedColumns.includes(col.label));

  const dataRows = allData.map((row) =>
    visible.map((col) => {
      const value = row[col.key];
      if (col.exportFormat) return col.exportFormat(value, row);
      if (col.format) {
        const result = col.format(value, row);
        if (typeof result === 'string' || typeof result === 'number') return String(result);
      }
      if (value == null || value === '') return '-';
      return String(value);
    }),
  );

  if (totalsRowBuilder) return [totalsRowBuilder(visible), ...dataRows];

  const hasSummable = visible.some((col) => col.summable);
  if (!hasSummable) return dataRows;

  const totalsRow = visible.map((col, i) => {
    if (i === 0) return 'Total';
    if (!col.summable) return '';
    const total = allData.reduce((sum, row) => {
      const val = Number(row[col.key]);
      return Number.isNaN(val) ? sum : sum + val;
    }, 0);
    if (col.exportFormat) return col.exportFormat(total, {} as any);
    if (col.format) {
      const result = col.format(total, {} as any);
      if (typeof result === 'string' || typeof result === 'number') return String(result);
    }
    return total.toFixed(2);
  });

  return [totalsRow, ...dataRows];
};

export const ExportPrintButtons: FC<Props> = ({ title, fontSize, exportFileName, columns, allData, selectedColumns, totalsRowBuilder, content, printColumnNumber, enableButtons, translation, reportNameInEn, sendEmail }) => {
  const classes = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const { locale } = useAppData();
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const visible = selectedColumns.length === 0 ? columns : columns.filter((col) => selectedColumns.includes(col.label));
  const capitalizedHeaders = () => visible.map((col) => col.label?.toUpperCase());

  const handleExportCSVClick = () => {
    trackAmplitudeData('partnerPortal_action_report', {
      type: 'ExportExcel',
      report_name: reportNameInEn,
    });
    const exportData = buildExportData(allData, columns, selectedColumns, totalsRowBuilder);
    const builder = new CsvBuilder(`${exportFileName}.csv`);
    builder.setDelimeter(',').setColumns(capitalizedHeaders()).addRows(exportData).exportFile();
  };

  const createPDF = (): Promise<any> =>
    new Promise((resolve) => {
      if (jsPDF !== null) {
        const exportData = buildExportData(allData, columns, selectedColumns, totalsRowBuilder);
        const content: {
          startY: number;
          head: string[][];
          body: string[][];
          margin: {
            top: number;
            right: number;
            bottom: number;
            left: number;
          };
          styles: {
            fontSize: number;
            font?: string;
            cellPadding: number;
            overflow: 'ellipsize';
            valign: 'middle';
          };
          headStyles: {
            fontSize: number;
            cellPadding: number;
            overflow: 'ellipsize';
            valign: 'middle';
          };
          tableWidth: 'auto';
        } = {
          startY: 30,
          head: [capitalizedHeaders()],
          body: exportData,
          margin: {
            top: 20,
            right: 10,
            bottom: 20,
            left: 10,
          },
          styles: {
            fontSize: fontSize || 7,
            cellPadding: 2,
            overflow: 'ellipsize',
            valign: 'middle',
          },
          headStyles: {
            fontSize: fontSize || 7,
            cellPadding: 2,
            overflow: 'ellipsize',
            valign: 'middle',
          },
          tableWidth: 'auto',
        };

        const unit = 'pt';
        const size = 'A4';
        const orientation = 'landscape';

        // eslint-disable-next-line new-cap
        const doc = new jsPDF(orientation, unit, size);

        if (locale === 'ja') {
          doc.addFont('/NotoSansJP-Regular.ttf', 'NotoSansJP-Regular', 'regular');
          doc.setFont('NotoSansJP-Regular', 'regular');
          content.styles.font = 'NotoSansJP-Regular';
        }

        if (locale === 'ar') {
          doc.addFont('/Amiri-Regular.ttf', 'Amiri-Regular', 'regular');
          doc.setFont('Amiri-Regular', 'regular');
          content.styles.font = 'Amiri-Regular';
        }

        if (locale === 'zh') {
          doc.addFont('/PingFang-SC-Regular.ttf', 'PingFang-SC-Regular', 'regular');
          doc.setFont('PingFang-SC-Regular', 'regular');
          content.styles.font = 'PingFang-SC-Regular';
        }

        if (locale === 'th') {
          doc.addFont('/ThonburiBold.ttf', 'ThonburiBold', 'bold');
          doc.setFont('ThonburiBold', 'bold');
          content.styles.font = 'ThonburiBold';
        }

        if (locale === 'ko') {
          doc.addFont('/NotoSansKR-Regular.ttf', 'NotoSansKR-Regular', 'regular');
          doc.setFont('NotoSansKR-Regular', 'regular');
          content.styles.font = 'NotoSansKR-Regular';
        }

        if (locale === 'vi') {
          doc.addFont('/Roboto-Regular.ttf', 'Roboto-Regular', 'regular');
          doc.setFont('Roboto-Regular', 'regular');
          content.styles.font = 'Roboto-Regular';
        }

        autoTable(doc, content);

        setOpen(false);
        doc.save(`${exportFileName}.pdf`);
        setTimeout(() => {
          resolve('resolved');
        }, 3000);
      }
    });

  const handleExportPDFClick = () => {
    setLoading(true);
    createPDF().then(() => {
      trackAmplitudeData('partnerPortal_action_report', {
        type: 'ExportPDF',
        report_name: reportNameInEn,
      });
      setOpen(false);
      setLoading(false);
    });
  };

  return (
    <>
      <ButtonGroup ref={anchorRef} className={classes.buttonGroup}>
        <Button className={classes.btn} aria-expanded={open ? 'true' : undefined} onClick={handleToggle} disabled={!enableButtons}>
          <ExportIcon />
        </Button>
      </ButtonGroup>
      <Popper open={open} anchorEl={anchorRef.current} transition style={{ zIndex: 1000 }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList className={classes.menuList}>
                  <MenuItem className={classes.menuItem} onClick={handleExportPDFClick} disabled={loading}>
                    <PDFFileIcon />
                    <span className={classes.menuItemText}>{translation.downloadPDF}</span>
                  </MenuItem>
                  <MenuItem className={classes.menuItem} onClick={handleExportCSVClick}>
                    <ExcelFileIcon />
                    <span className={classes.menuItemText}>{translation.downloadExcel}</span>
                  </MenuItem>
                  {(sendEmail || content) && (
                    <>
                      <span className={classes.menuTitle}>{translation.other_options}</span>
                      {sendEmail && (
                        <MenuItem className={classes.menuItem} onClick={sendEmail}>
                          <SendEmailIcon />
                          <span className={classes.menuItemText}>{translation.text_6412}</span>
                        </MenuItem>
                      )}
                      {content && (
                        <PrintButton translation={translation} title={title} columnNumber={printColumnNumber || 0} reportNameInEn={reportNameInEn}>
                          {content}
                        </PrintButton>
                      )}
                    </>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
